/** Calendar-year helpers for Executive Pulse. Mirrors backend `executive-pulse-reporting-period.js`. */

export function getCalendarYear(today = new Date()) {
    return today.getFullYear();
}

/** @deprecated Use getCalendarYear */
export function getCalendarQuarter(today = new Date()) {
    const year = today.getFullYear();
    const quarter = Math.floor(today.getMonth() / 3) + 1;
    return { year, quarter };
}

/**
 * @param {URLSearchParams} searchParams
 * @param {Date} [today]
 */
export function resolvePeriodFromSearchParams(
    searchParams,
    today = new Date()
) {
    const y = Number.parseInt(searchParams.get('year') || '', 10);
    return {
        year:
            Number.isFinite(y) && y >= 2000 && y <= 2100
                ? y
                : getCalendarYear(today)
    };
}

/**
 * @param {number} year
 * @param {Date} [today]
 */
export function buildReportingPeriod(year, today = new Date()) {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31, 23, 59, 59, 999);
    const todayDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
    );
    const cy = today.getFullYear();
    const isCurrentYear = year === cy;
    const chartEnd = isCurrentYear && todayDay <= end ? todayDay : end;
    const isFutureYear = todayDay < start;

    return {
        year,
        label: String(year),
        start,
        end,
        chartEnd,
        chartStartIso: start.toISOString(),
        chartEndIso: chartEnd.toISOString(),
        periodEndIso: end.toISOString(),
        quarterEndIso: end.toISOString(),
        isCurrentYear,
        isFutureYear,
        isCurrentQuarter: isCurrentYear,
        isFutureQuarter: isFutureYear
    };
}

/**
 * @param {ReturnType<typeof buildReportingPeriod>} period
 */
export function reportingPeriodToClientMeta(period) {
    return {
        year: period.year,
        label: period.label,
        startDate: period.start.toISOString(),
        endDate: period.end.toISOString(),
        chartEndDate: period.chartEnd.toISOString(),
        isCurrentYear: period.isCurrentYear,
        isFutureYear: period.isFutureYear,
        isCurrentQuarter: period.isCurrentYear,
        isFutureQuarter: period.isFutureYear
    };
}

/**
 * @param {Date} [anchor]
 * @param {number} [pastCount] earlier years including anchor
 * @param {number} [futureCount] later years after anchor
 */
export function buildYearOptions(
    anchor = new Date(),
    pastCount = 8,
    futureCount = 1
) {
    const cur = getCalendarYear(anchor);
    const out = [];
    for (let y = cur - pastCount; y <= cur + futureCount; y += 1) {
        out.push({ year: y, label: String(y) });
    }
    return out;
}

/** @deprecated Use buildYearOptions */
export function buildQuarterOptions(
    anchor = new Date(),
    pastCount = 12,
    futureCount = 0
) {
    return buildYearOptions(anchor, Math.ceil(pastCount / 4), futureCount);
}

/**
 * @param {{ startDate?: string, endDate?: string, chartEndDate?: string }} meta — `meta.reportingPeriod`
 */
export function formatReportingPeriodDateRange(meta) {
    if (!meta?.startDate) return '';
    const start = new Date(meta.startDate);
    const end = new Date(meta.chartEndDate || meta.endDate || meta.startDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return '';
    }
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${start.toLocaleDateString(
        undefined,
        opts
    )} – ${end.toLocaleDateString(undefined, opts)}`;
}
